import { Modal, Form, Input, InputNumber, Select, DatePicker, Button, Row, Col, Divider } from "antd";
import { MinusCircleOutlined, PlusOutlined } from "@ant-design/icons";
import { useEffect, useState } from "react";
import { IBillingCompletedPayload } from "store/po/poSlice";
import { Moment } from "moment";

const PAYMENT_MODES = ["NEFT", "RTGS", "IMPS", "UPI", "CHEQUE", "CASH"];
const PAYMENT_STATUSES = ["PAID", "PARTIALLY_PAID", "PENDING"];

interface IPaymentFormValues {
  payments: {
    amount: number;
    paymentMode: string;
    paymentRef: string;
    paymentDate: Moment;
    status: string;
    remarks?: string;
  }[];
  notes?: string;
}

interface IProps {
  visible: boolean;
  poId?: string;
  loading: boolean;
  onSubmit: (payload: IBillingCompletedPayload) => void;
  onCancel: () => void;
}

export const CompleteBillingModal = ({ visible, poId, loading, onSubmit, onCancel }: IProps) => {
  const [form] = Form.useForm<IPaymentFormValues>();
  const [totalPaid, setTotalPaid] = useState(0);

  useEffect(() => {
    if (visible) {
      form.resetFields();
      setTotalPaid(0);
    }
  }, [visible, form]);

  const recomputeTotal = () => {
    const payments: IPaymentFormValues["payments"] = form.getFieldValue("payments") || [];
    const total = payments.reduce((sum, p) => sum + (Number(p?.amount) || 0), 0);
    setTotalPaid(Number(total.toFixed(2)));
  };

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      const payload: IBillingCompletedPayload = {
        payments: values.payments.map((p) => ({
          amount: Number(p.amount),
          paymentMode: p.paymentMode,
          paymentRef: p.paymentRef,
          paymentDate: p.paymentDate.format("YYYY-MM-DD"),
          status: p.status,
          remarks: p.remarks || "",
        })),
        totalPaid: Number(
          values.payments.reduce((sum, p) => sum + (Number(p.amount) || 0), 0).toFixed(2)
        ),
        notes: values.notes || "",
      };
      onSubmit(payload);
    } catch {
      // validation errors are shown inline by the form
    }
  };

  return (
    <Modal
      title={`Complete Billing - PO ${poId || ""}`}
      visible={visible}
      onCancel={onCancel}
      footer={
        <Row justify="end" gutter={16}>
          <Col>
            <Button onClick={onCancel} disabled={loading}>
              Cancel
            </Button>
          </Col>
          <Col>
            <Button type="primary" loading={loading} onClick={handleOk}>
              Mark Billing Completed
            </Button>
          </Col>
        </Row>
      }
      width={900}
      maskClosable={false}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{ payments: [{}] }}
        onValuesChange={recomputeTotal}
      >
        <Form.List name="payments">
          {(fields, { add, remove }) => (
            <>
              {fields.map(({ key, name, ...restField }) => (
                <div key={key}>
                  <Row gutter={8} align="bottom">
                    <Col span={4}>
                      <Form.Item
                        {...restField}
                        name={[name, "amount"]}
                        label="Amount"
                        rules={[{ required: true, message: "Amount is required" }]}
                      >
                        <InputNumber
                          min={0.01}
                          precision={2}
                          style={{ width: "100%" }}
                          placeholder="0.00"
                        />
                      </Form.Item>
                    </Col>
                    <Col span={4}>
                      <Form.Item
                        {...restField}
                        name={[name, "paymentMode"]}
                        label="Payment Mode"
                        rules={[{ required: true, message: "Mode is required" }]}
                      >
                        <Select placeholder="Select mode">
                          {PAYMENT_MODES.map((mode) => (
                            <Select.Option key={mode} value={mode}>
                              {mode}
                            </Select.Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col span={6}>
                      <Form.Item
                        {...restField}
                        name={[name, "paymentRef"]}
                        label="Payment Ref"
                        rules={[{ required: true, message: "Reference is required" }]}
                      >
                        <Input placeholder="e.g. NEFT20260622" />
                      </Form.Item>
                    </Col>
                    <Col span={4}>
                      <Form.Item
                        {...restField}
                        name={[name, "paymentDate"]}
                        label="Payment Date"
                        rules={[{ required: true, message: "Date is required" }]}
                      >
                        <DatePicker style={{ width: "100%" }} format="YYYY-MM-DD" />
                      </Form.Item>
                    </Col>
                    <Col span={4}>
                      <Form.Item
                        {...restField}
                        name={[name, "status"]}
                        label="Status"
                        rules={[{ required: true, message: "Status is required" }]}
                        initialValue="PAID"
                      >
                        <Select>
                          {PAYMENT_STATUSES.map((s) => (
                            <Select.Option key={s} value={s}>
                              {s}
                            </Select.Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col span={2}>
                      {fields.length > 1 && (
                        <Form.Item label=" ">
                          <MinusCircleOutlined
                            onClick={() => {
                              remove(name);
                              // form values update after remove on next tick
                              setTimeout(recomputeTotal, 0);
                            }}
                          />
                        </Form.Item>
                      )}
                    </Col>
                  </Row>
                  <Row>
                    <Col span={24}>
                      <Form.Item {...restField} name={[name, "remarks"]} label="Remarks">
                        <Input placeholder="Remarks" />
                      </Form.Item>
                    </Col>
                  </Row>
                </div>
              ))}
              <Form.Item>
                <Button type="dashed" onClick={() => add({ status: "PAID" })} block icon={<PlusOutlined />}>
                  Add Payment
                </Button>
              </Form.Item>
            </>
          )}
        </Form.List>
        <Divider style={{ margin: "0 0 1rem" }} />
        <Row gutter={16}>
          <Col span={6}>
            <Form.Item label="Total Paid">
              <InputNumber value={totalPaid} precision={2} disabled style={{ width: "100%" }} />
            </Form.Item>
          </Col>
          <Col span={18}>
            <Form.Item name="notes" label="Notes">
              <Input.TextArea rows={1} placeholder="Notes (optional)" />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};
